import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  EditorState,
  AtomicBlockUtils,
  Modifier,
  RichUtils,
  SelectionState,
} from 'draft-js';
import GeckoRequest from 'services/gecko-request';
import TemplateTags from 'services/template-tags';
import { v4 as uuid } from 'uuid';

const insertArrayAfterIndex = (array, arrayToInsert, index) => {
  return array.reduce((items, item, currentIndex) => {
    if (index === currentIndex) {
      return items.concat(item, arrayToInsert);
    }
    return items.concat(item);
  }, []);
};

const insertArrayReplacingIndex = (array, arrayToInsert, index) => {
  const before = array.slice(0, index);
  const after = array.slice(index + 1, array.length);
  return [...before, ...arrayToInsert, ...after];
};

const makeEditorObjectUnique = (editorObject) => {
  // Give each entity a new unique key and keep track of the old ones
  const [entityMap, oldEntityKeyMap] = Object.entries(
    editorObject.entityMap
  ).reduce(
    ([entityMap, keyMap], entry) => {
      const [key, entity] = entry;
      const newEntityKey = uuid();
      const newEntityMap = { ...entityMap, [newEntityKey]: entity };
      const newKeyMap = { ...keyMap, [key]: newEntityKey };
      return [newEntityMap, newKeyMap];
    },
    [{}, {}]
  );

  // Give each block new unique key and replace any entity range keys with the new ones from above
  const blocks = editorObject.blocks.map((block) => {
    const newBlockKey = uuid();
    const entityRanges = block.entityRanges.map((range) => {
      const newEntityKey = oldEntityKeyMap[range.key];
      return { ...range, key: newEntityKey };
    });
    return { ...block, entityRanges, key: newBlockKey };
  });

  return {
    ...editorObject,
    blocks,
    entityMap,
  };
};

const swapAttachmentsForPublicURLS = async (editorContent) => {
  const attachmentEntities = Object.entries(editorContent.entityMap).filter(
    ([_key, value]) => {
      return value.type === 'ATTACHMENT';
    }
  );
  return attachmentEntities.reduce(
    async (editorContentPromise, [entityKey, entityValue]) => {
      const current = await editorContentPromise;
      const { url } = await GeckoRequest.post(
        '/message/get-shortened-attachment',
        {
          attachment: entityValue.data,
        }
      );
      const blocks = current.blocks.map((block) => {
        const entityRange = block.entityRanges[0];
        const containsEntity =
          block.type === 'atomic' && String(entityRange?.key) === entityKey;
        if (containsEntity === false) {
          return block;
        }
        const textBefore = block.text.slice(0, entityRange.offset);
        const textAfter = block.text.slice(
          entityRange.length,
          block.text.length
        );
        const newText = `${textBefore}${url}${textAfter}`;
        const newBlock = {
          ...block,
          text: newText,
          type: 'unstyled',
          entityRanges: [],
        };
        return newBlock;
      });
      const entityMap = { ...current.entityMap };
      delete entityMap[entityKey];
      return {
        blocks,
        entityMap,
      };
    },
    editorContent
  );
};

// adds rich text saved replies to message content. returns a ContentState object
export const addReplyToContent = async (reply, { editorState, editorMode }) => {
  const currentContent = convertToRaw(editorState.getCurrentContent());
  const selection = editorState.getSelection();
  const anchorKey = selection.getAnchorKey();

  const replyContent = makeEditorObjectUnique(reply.editor_object);
  const insertIndex = currentContent.blocks.findIndex(
    (block) => block.key === anchorKey
  );
  const currentBlock = currentContent.blocks[insertIndex];

  const newBlocks =
    currentBlock.text.length === 0
      ? insertArrayReplacingIndex(
          currentContent.blocks,
          replyContent.blocks,
          insertIndex
        )
      : insertArrayAfterIndex(
          currentContent.blocks,
          replyContent.blocks,
          insertIndex
        );

  const currentEntities = currentContent.entityMap;
  const replyEntities = replyContent.entityMap;
  const newEntityMap = { ...currentEntities, ...replyEntities };

  const newContent = {
    blocks: newBlocks,
    entityMap: newEntityMap,
  };

  const lastSavedReplyBlock =
    replyContent.blocks[replyContent.blocks.length - 1];
  const newSelection = SelectionState.createEmpty(
    lastSavedReplyBlock.key
  ).merge({
    anchorOffset: lastSavedReplyBlock.text.length,
    focusOffset: lastSavedReplyBlock.text.length,
  });

  if (editorMode === 'plain') {
    const withReplacedAttachments = await swapAttachmentsForPublicURLS(
      newContent
    );
    return [
      convertFromRaw(
        imagesToLinks(linksToPlaintext(withReplacedAttachments), 'IMAGE')
      ),
      newSelection,
    ];
  }

  return [convertFromRaw(newContent), newSelection];
};

// this is called when the user hits space after entering '-', '*' or '1.' at the start of a line
// it removes the existing content from the line and returns an updated EditorState
export const clearBlockContent = (blockType, editorState) => {
  const blockKey = editorState.getSelection().getAnchorKey();
  let newerContentState = editorState.getCurrentContent();
  const blockSelection = SelectionState.createEmpty(blockKey);

  let newFocusOffset;
  let newAnchorOffset;

  if (blockType === 'unordered-list-item') {
    newAnchorOffset = 0;
    newFocusOffset = 1;
  } else if (blockType === 'ordered-list-item') {
    newAnchorOffset = 0;
    newFocusOffset = 2;
  } else {
    // 'unstyled'
    newAnchorOffset = 0;
    newFocusOffset = 0;
  }

  const updatedSelection = blockSelection.merge({
    focusKey: blockKey,
    focusOffset: newFocusOffset,
    anchorOffset: newAnchorOffset,
    hasFocus: true,
  });

  newerContentState = Modifier.replaceText(
    newerContentState,
    updatedSelection,
    ''
  );

  const newEditorState = EditorState.push(
    editorState,
    newerContentState,
    'insert-characters'
  );
  return newEditorState;
};

// clears all content from the editor and sets the first block type to unstyled
export const clearEditor = (editorState) => {
  let contentState = editorState.getCurrentContent();
  const firstBlock = contentState.getFirstBlock();
  const lastBlock = contentState.getLastBlock();
  const allSelected = new SelectionState({
    anchorKey: firstBlock.getKey(),
    anchorOffset: 0,
    focusKey: lastBlock.getKey(),
    focusOffset: lastBlock.getLength(),
    hasFocus: true,
  });
  contentState = Modifier.removeRange(contentState, allSelected, 'backward');
  const newEditorState = EditorState.push(
    editorState,
    contentState,
    'remove-range'
  );
  return toggleBlockType('unstyled', newEditorState);
};

// called when user requests message be sent
export const composeSubmissionObject = (
  editorState,
  editorMode,
  attachments,
  props,
  noteChannel,
  noteMode,
  metadata = { excludeSignature: false }
) => {
  let content = editorState.getCurrentContent().getPlainText();
  let editor_object = convertToRaw(editorState.getCurrentContent());

  if (editorMode === 'plain' && attachments.length > 0) {
    content = '';
    editor_object = null;
  }
  return {
    recipient_channel_id: noteMode.on
      ? Number(noteChannel.id)
      : props.recipientChannelId,
    noteMode: noteMode.on,
    content: content,
    editor_object: editor_object,
    metadata: metadata,
    attachments: attachments.length > 0 ? attachments : null,
  };
};

export const cursorToBlockEnd = (editorState) => {
  const currentBlockKey = editorState.getSelection().getAnchorKey();
  const currentBlock = editorState
    .getCurrentContent()
    .getBlockForKey(currentBlockKey);
  let selection = SelectionState.createEmpty(currentBlockKey);
  let length = currentBlock.getLength();
  selection = selection.merge({
    hasFocus: true,
    isBackward: false,
    anchorOffset: length,
    focusOffset: length,
  });
  return EditorState.forceSelection(editorState, selection);
};

export const linksToPlaintext = (rawContent) => {
  let newEntities = rawContent.entityMap;
  let additionalLength = 0;
  let newBlocks = rawContent.blocks.map((block) => {
    let newBlock = Object.assign({}, block);
    if (block.entityRanges) {
      let links = block.entityRanges.map((range) => {
        const entity = rawContent.entityMap[range.key];
        if (entity.type === 'LINK') {
          const offsetStart = range.offset + additionalLength;
          const offsetEnd = offsetStart + range.length;
          const linkText = newBlock.text.slice(offsetStart, offsetEnd);
          // returns early if link text and url are the same
          if (linkText === entity.data.url)
            return delete newEntities[range.key];
          const insertUrl = ` (${entity.data.url})`;
          additionalLength += insertUrl.length;
          newBlock.text = [
            newBlock.text.slice(0, offsetEnd),
            insertUrl,
            newBlock.text.slice(offsetEnd),
          ].join('');
          delete newEntities[range.key];
        }
      });
    }
    return newBlock;
  });
  return {
    blocks: newBlocks,
    entityMap: newEntities,
  };
};

// given raw editor content, converts image, video or audio entities within it to links, returning
// new raw editor content
export const imagesToLinks = (rawContent, entityToReplace) => {
  let newContent = {};
  let entities = rawContent.entityMap;
  let newEntities = {};
  for (let i = 0; i < Object.keys(entities).length; i++) {
    let newEntity = {};
    if (entities[Object.keys(entities)[i]].type === entityToReplace) {
      newEntity.type = 'LINK';
      newEntity.mutability = 'MUTABLE';
      newEntity.data = {
        url: entities[Object.keys(entities)[i]].data.src,
        targetOption: '_blank',
      };
      newEntities[Object.keys(entities)[i]] = newEntity;
    } else {
      newEntities[Object.keys(entities)[i]] =
        entities[Object.keys(entities)[i]];
    }
  }
  newContent.blocks = rawContent.blocks;
  newContent.entityMap = newEntities;
  return newContent;
};

export const initialState = (props) => {
  const channels = props.availableChannels || [{ type: 'live_chat', id: '0' }];
  const recipientChannelId = (props) => {
    let id;
    if (props.recipientChannel) {
      id = Number(props.recipientChannel.id);
    } else if (channels.length) {
      id = Number(channels[0].id);
    } else {
      id = 0;
    }
    return id;
  };
  return {
    availableChannels: channels,
    editorMode: props.editorMode || 'rich',
    editorState: props.editorState || EditorState.createEmpty(),
    noteMode: props.noteMode || { on: false },
    recipientChannelId: recipientChannelId(props),
    suggestions: [],
  };
};

const moveCursorToNextEmptySpace = (editorState) => {
  const selection = editorState.getSelection();
  if (isTextSelected(editorState)) {
    // If there is selected text, remove it to move the cursor to the end of the selection
    const withoutSelection = Modifier.removeRange(
      editorState.getCurrentContent(),
      selection,
      'backward'
    );
    const newEditorState = EditorState.push(
      editorState,
      withoutSelection,
      'remove-range'
    );
    return EditorState.moveFocusToEnd(newEditorState);
  } else {
    // If there is no selected text, find the next empty space
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(selection.getStartKey());
    const currentOffset = selection.getStartOffset();
    const blockText = currentBlock.getText();

    // Find the next empty space starting from the current offset
    const nextEmptySpaceIndex = blockText.indexOf(' ', currentOffset);

    if (nextEmptySpaceIndex !== -1) {
      // Move the cursor to the next empty space
      const newSelection = selection.merge({
        anchorOffset: nextEmptySpaceIndex + 1,
        focusOffset: nextEmptySpaceIndex + 1,
      });
      return EditorState.forceSelection(editorState, newSelection);
    } else {
      // If there are no more empty spaces in this block, move to the next block
      const nextBlock = contentState.getBlockAfter(currentBlock.getKey());
      if (nextBlock) {
        // Move the cursor to the start of the next block
        const newSelection = SelectionState.createEmpty(nextBlock.getKey()).set(
          'hasFocus',
          true
        );
        return EditorState.forceSelection(editorState, newSelection);
      }
      // If there are no more blocks, return the original editor state.
      return editorState;
    }
  }
};

const isTextSelected = (editorState) =>
  !editorState.getSelection().isCollapsed();

export const insertPlainText = (text, editorState) => {
  const selectionCompensatedState = isTextSelected(editorState)
    ? moveCursorToNextEmptySpace(editorState)
    : editorState;
  const newContent = Modifier.insertText(
    selectionCompensatedState.getCurrentContent(),
    selectionCompensatedState.getSelection(),
    text
  );
  return EditorState.push(
    selectionCompensatedState,
    newContent,
    'insert-characters'
  );
};

export const insertBotSuggestion = async (
  suggestion,
  { editorState, editorMode }
) => {
  const [newContent, newSelection] = await addReplyToContent(suggestion, {
    editorState,
    editorMode,
  });
  const newEditorState = EditorState.forceSelection(
    EditorState.push(editorState, newContent, 'insert-fragment'),
    newSelection
  );
  return newEditorState;
};

export const insertSavedReply = async (reply, { editorState, editorMode }) => {
  if (!reply.editor_object) {
    // older replies won't have an editor object
    return insertPlainText(reply.contents, editorState);
  }
  const [newContent, newSelection] = await addReplyToContent(reply, {
    editorState,
    editorMode,
  });
  const newEditorState = EditorState.forceSelection(
    EditorState.push(editorState, newContent, 'insert-fragment'),
    newSelection
  );
  return newEditorState;
};

export const insertLink = async (text, url, { editorState, editorMode }) => {
  const editor_object = {
    blocks: [
      {
        key: uuid(),
        data: {},
        text,
        type: 'unstyled',
        depth: 0,
        entityRanges: [
          {
            key: 0,
            length: text.length,
            offset: 0,
          },
        ],
        inlineStyleRanges: [],
      },
    ],
    entityMap: {
      0: {
        data: {
          url,
          targetOption: '_blank',
        },
        type: 'LINK',
        mutability: 'MUTABLE',
      },
    },
  };
  const [newContent, newSelection] = await addReplyToContent(
    { editor_object },
    {
      editorState,
      editorMode,
    }
  );
  const newEditorState = EditorState.forceSelection(
    EditorState.push(editorState, newContent, 'insert-fragment'),
    newSelection
  );
  return newEditorState;
};

export const insertEmoji = async (emoji, { editorState }) =>
  insertPlainText(emoji, editorState);

export const insertAISuggestion = async (
  aiSuggestion,
  { editorState, editorMode }
) => {
  const [newContent, newSelection] = await addReplyToContent(aiSuggestion, {
    editorState,
    editorMode,
  });
  const newEditorState = EditorState.forceSelection(
    EditorState.push(editorState, newContent, 'insert-fragment'),
    newSelection
  );
  return newEditorState;
};
export const insertTag = (
  tag,
  editorState,
  { templateContext, compileTemplateTags }
) => {
  const newContent = Modifier.replaceText(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    compileTemplateTags ? TemplateTags(tag, templateContext) : tag
  );
  return EditorState.push(editorState, newContent, 'insert-Characters');
};

export const isImageLink = (entity) => {
  let url = entity.data.url;
  let ext = url.split('.').pop().toUpperCase();
  return (
    entity.type === 'LINK' &&
    ['JPG', 'JPEG', 'WEBP', 'GIF', 'PNG', 'ICO'].indexOf(ext) > -1
  );
};

export const isValidMessage = (editorState, attachments) => {
  const hasRichText = editorState.getCurrentContent().hasText();
  const plainTextHasAttachments = attachments.length > 0;
  return hasRichText || plainTextHasAttachments;
};

const negate =
  (func) =>
  (...args) =>
    !func(...args);
export const isInvalidMessage = negate(isValidMessage);

export const toggleList = (event, editorState) => {
  event.preventDefault();
  let newEditorState = RichUtils.toggleBlockType(editorState, 'unstyled');
  return EditorState.push(
    editorState,
    newEditorState.getCurrentContent(),
    'change-block-type'
  );
};

export const transformState = async (transition, editorState) => {
  let newEditorState;
  let newContent;
  let content;
  let noVideo;
  let noMedia;
  let noImage;
  switch (transition) {
    case 'rich-semi-rich':
      content = convertToRaw(editorState.getCurrentContent());
      noVideo = imagesToLinks(content, 'VIDEO');
      noMedia = imagesToLinks(noVideo, 'AUDIO');
      newContent = convertFromRaw(noMedia);
      newEditorState = EditorState.push(
        editorState,
        newContent,
        'insert-fragment'
      );
      break;
    case 'rich-plain':
    case 'semi-rich-plain':
      content = convertToRaw(editorState.getCurrentContent());
      noImage = imagesToLinks(content, 'IMAGE');
      noVideo = imagesToLinks(noImage, 'VIDEO');
      noMedia = imagesToLinks(noVideo, 'AUDIO');
      newContent = convertFromRaw(await swapAttachmentsForPublicURLS(noMedia));
      const noMediaState = EditorState.push(
        editorState,
        newContent,
        'insert-fragment'
      );
      newEditorState = EditorState.push(
        noMediaState,
        ContentState.createFromText(
          noMediaState.getCurrentContent().getPlainText()
        ),
        'insert-fragment'
      );
      break;
    default:
      newEditorState = editorState;
  }
  return newEditorState;
};

export const stateFromNewMode = async (
  previousEditorMode,
  nextEditorMode,
  editorState
) => {
  const transition = `${previousEditorMode}-${nextEditorMode}`;
  if (transition === 'plain-semi-rich' || transition === 'plain-rich') {
    return {
      attachments: [],
    };
  } else {
    return {
      editorState: await transformState(transition, editorState),
    };
  }
};

export const toggleBlockType = (type, editorState) => {
  let newEditorState = editorState;
  newEditorState = RichUtils.toggleBlockType(newEditorState, type);

  newEditorState = EditorState.push(
    editorState,
    newEditorState.getCurrentContent(),
    'change-block-type'
  );
  return newEditorState;
};

const createAttachmentEntity = (attachment) => {
  if (typeof attachment === 'string') {
    const urlDetails = new URL(attachment);
    const ext = urlDetails.pathname.split('.').pop().toUpperCase();
    if (['JPG', 'JPEG', 'WEBP', 'GIF', 'PNG', 'ICO'].includes(ext)) {
      const data = {
        src: attachment,
        ext,
      };
      return ['IMAGE', 'IMMUTABLE', data];
    }
    if (['MOV', 'MP4', 'AVI'].includes(ext)) {
      const data = {
        src: attachment,
        controls: true,
        ext,
      };
      return ['VIDEO', 'IMMUTABLE', data];
    }
    if (['MP3', 'WAV'].includes(ext)) {
      const data = {
        src: attachment,
        controls: true,
        ext,
      };
      return ['AUDIO', 'IMMUTABLE', data];
    }
    const data = {
      url: attachment,
      targetOption: '_blank',
    };
    return ['LINK', 'IMMUTABLE', data];
  }
  return ['ATTACHMENT', 'IMMUTABLE', attachment];
};

// inserts media entities into the message content
export const insertImageLink = (
  { urls: attachments },
  _editorMode,
  editorState
) => {
  return attachments.reduce((editorState, attachment) => {
    const entity = createAttachmentEntity(attachment);
    const entityKey = editorState
      .getCurrentContent()
      .createEntity(...entity)
      .getLastCreatedEntityKey();

    let newEditorState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      attachment.fileName || attachment
    );

    return EditorState.push(
      editorState,
      newEditorState.getCurrentContent(),
      'apply-entity'
    );
  }, editorState);
};

export const stateFromNewContext = (nextProps, state) => {
  const nextChannels = nextProps.availableChannels;
  const nextRecipientChannel = nextProps.recipientChannel || nextChannels[0];
  let newEditorState = nextProps.editorState;
  return {
    availableChannels: nextChannels,
    editorMode: nextProps.editorMode || 'rich',
    forwardShowing: false,
  };
};

const isTwilioChannel = ({ type }) => type === 'twilio' || type === 'whatsapp';

export const isTooLong = (editorState, channel) => {
  console.log(channel);
  const messageLength = editorState
    .getCurrentContent()
    .getPlainText(' ').length;
  return isTwilioChannel(channel) && messageLength > 1600;
};

export const RBfuncs = {
  addReplyToContent,
  clearBlockContent,
  clearEditor,
  composeSubmissionObject,
  cursorToBlockEnd,
  imagesToLinks,
  initialState,
  insertBotSuggestion,
  insertPlainText,
  insertTag,
  insertSavedReply,
  insertEmoji,
  isImageLink,
  isValidMessage,
  isInvalidMessage,
  toggleList,
  transformState,
  stateFromNewMode,
  toggleBlockType,
  insertImageLink,
  stateFromNewContext,
  isTooLong,
  insertAISuggestion,
  insertLink,
};
